import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { isRoleOfType, RoleType } from '@iss/libs/permissions';
import { Role } from '@iss/libs/graphql';

import { AppContextState } from './app-context.state';
import { appContextActions } from './app-context.actions';

import { findPreferred } from '@iss/wo-common/utils';
import { AsyncData, AsyncStatus, pickAsyncData } from '@iss/wo-common/types';
import { authFeature } from '@iss/wo-common/ngrx';

export const initialState: AppContextState = {
  permissions: { status: AsyncStatus.INITIAL },
  role: null,
  countries: { status: AsyncStatus.INITIAL },
  country: null,
  contextAccepted: false,
  isMobile: null,
  customer: null,
  onlyOneCustomer: { status: AsyncStatus.INITIAL },
  site: null,
  onlyOneSite: { status: AsyncStatus.INITIAL },
  serviceLocation: null,
  onlyOneServiceLocation: { status: AsyncStatus.INITIAL },
  supplier: null,
};

export const appContextFeature = createFeature({
  name: 'appContext',
  reducer: createReducer(
    initialState,
    on(appContextActions.mobileDeviceDetected, (state, { isMobile }): AppContextState  => ({
      ...state,
      isMobile,
    })),
    on(appContextActions.roleSelected, (state, { role }): AppContextState => ({
      ...state,
      role,
    })),
    on(appContextActions.countrySelected, (state, { country }): AppContextState => ({
      ...state,
      country,
    })),
    on(appContextActions.customerSelected, (state, { customer }): AppContextState => ({
      ...state,
      customer,
      site: null,
      serviceLocation: null,
    })),
    on(appContextActions.onlyOneCustomerLoadingFailure, (state): AppContextState => ({
      ...state,
      onlyOneCustomer: { status: AsyncStatus.FAILURE },
    })),
    on(appContextActions.onlyOneCustomerLoading, (state): AppContextState => ({
      ...state,
      onlyOneCustomer: { status: AsyncStatus.LOADING },
    })),
    on(appContextActions.onlyOneCustomerLoaded, (state, { customer }): AppContextState => ({
      ...state,
      onlyOneCustomer: {
        status: AsyncStatus.SUCCESS,
        data: customer,
      },
    })),
    on(appContextActions.siteSelected, (state, { site }): AppContextState  => ({
      ...state,
      site,
      serviceLocation: null,
    })),
    on(appContextActions.onlyOneSiteLoadingFailure, (state): AppContextState => ({
      ...state,
      onlyOneSite: { status: AsyncStatus.FAILURE },
    })),
    on(appContextActions.onlyOneSiteLoading, (state): AppContextState => ({
      ...state,
      onlyOneSite: { status: AsyncStatus.LOADING },
    })),
    on(appContextActions.onlyOneSiteLoaded, (state, { site }): AppContextState => ({
      ...state,
      onlyOneSite: {
        status: AsyncStatus.SUCCESS,
        data: site,
      },
    })),
    on(appContextActions.serviceLocationSelected, (state, { serviceLocation }): AppContextState  => ({
      ...state,
      serviceLocation,
    })),
    on(appContextActions.onlyOneServiceLocationLoadingFailure, (state): AppContextState => ({
      ...state,
      onlyOneServiceLocation: { status: AsyncStatus.FAILURE },
    })),
    on(appContextActions.onlyOneServiceLocationLoading, (state): AppContextState => ({
      ...state,
      onlyOneServiceLocation: { status: AsyncStatus.LOADING },
    })),
    on(appContextActions.onlyOneServiceLocationLoaded, (state, { serviceLocation }): AppContextState => ({
      ...state,
      onlyOneServiceLocation: {
        status: AsyncStatus.SUCCESS,
        data: serviceLocation,
      },
    })),
    on(appContextActions.supplierSelected, (state, { supplier }): AppContextState  => ({
      ...state,
      supplier,
    })),
    on(appContextActions.contextAccepted, (state): AppContextState  => ({
      ...state,
      contextAccepted: true,
    })),
    on(appContextActions.permissionsLoadingFailure, (state): AppContextState => ({
      ...state,
      permissions: { status: AsyncStatus.FAILURE },
    })),
    on(appContextActions.permissionsLoading, (state): AppContextState => ({
      ...state,
      permissions: { status: AsyncStatus.LOADING },
    })),
    on(appContextActions.permissionsLoaded, (state, { permissions }): AppContextState => ({
      ...state,
      permissions: {
        status: AsyncStatus.SUCCESS,
        data: permissions,
      },
    })),
    on(appContextActions.countriesLoadingFailure, (state): AppContextState => ({
      ...state,
      countries: { status: AsyncStatus.FAILURE },
    })),
    on(appContextActions.countriesLoading, (state): AppContextState => ({
      ...state,
      countries: { status: AsyncStatus.LOADING },
    })),
    on(appContextActions.countriesLoaded, (state, { countries }): AppContextState => ({
      ...state,
      countries: {
        status: AsyncStatus.SUCCESS,
        data: countries,
      },
    })),
    on(appContextActions.resetCountries, (state): AppContextState => ({
      ...state,
      countries: { status: AsyncStatus.INITIAL },
    })),
  ),
  extraSelectors: self => {
    const selectRole = createSelector(
      authFeature.selectDistinctRoles,
      self.selectRole,
      findPreferred,
    );

    const selectSupplier = createSelector(
      selectRole,
      self.selectSupplier,
      authFeature.selectAvailableSuppliers,
      (role, supplier, available) =>
        isRoleOfType(role, RoleType.Supplier) ? findPreferred(available, supplier) : null,
    );

    const nullIfAll = (value: string | 'ALL' | null): string | null => value === 'ALL' ? null : value;

    const getOption = (role: Role | null, asyncData: AsyncData<string | null>, value: string | 'ALL' | null) => {
      const effective = value || pickAsyncData(asyncData, null);
      if (isRoleOfType(role, RoleType.Customer)) {
        return effective;
      }

      if (isRoleOfType(role, RoleType.Admin, RoleType.CentralTeam)) {
        return nullIfAll(effective);
      }

      return null;
    };

    const selectCustomerOption = createSelector(
      selectRole,
      self.selectOnlyOneCustomer,
      self.selectCustomer,
      getOption,
    );

    const selectCustomer = createSelector(
      selectCustomerOption,
      nullIfAll,
    );

    const selectSiteOption = createSelector(
      selectRole,
      self.selectOnlyOneSite,
      self.selectSite,
      getOption,
    );

    const selectSite = createSelector(
      selectSiteOption,
      nullIfAll,
    );

    const selectServiceLocationOption = createSelector(
      selectRole,
      self.selectOnlyOneServiceLocation,
      self.selectServiceLocation,
      getOption,
    );

    const selectServiceLocation = createSelector(
      selectServiceLocationOption,
      nullIfAll,
    );

    const selectCustomerChosen = createSelector(selectCustomer, Boolean);
    const selectSiteChosen = createSelector(selectSite, Boolean);
    const selectServiceLocationChosen = createSelector(selectServiceLocation, Boolean);
    const selectSupplierChosen = createSelector(selectSupplier, Boolean);

    const selectCountries = createSelector(
      self.selectCountries,
      countries => pickAsyncData(countries, []),
    );

    const selectCountryCodes = createSelector(
      selectCountries,
      countries => countries.map(country => country.code),
    );

    const selectCountry = createSelector(
      authFeature.selectDistinctCountries,
      self.selectCountry,
      findPreferred,
    );

    const selectCountryConfig = createSelector(
      selectCountries,
      selectCountry,
      (countries, country) => countries.find(({ code }) => code === country)?.config,
    );

    const selectHasMultipleCountries = createSelector(
      self.selectCountries,
      countries => pickAsyncData(countries, []).length > 1,
    );

    const selectHasRequiredContext = createSelector(
      self.selectPermissions,
      self.selectIsMobile,
      self.selectContextAccepted,
      selectRole,
      selectCustomerChosen,
      selectSiteChosen,
      selectServiceLocationChosen,
      selectSupplierChosen,
      self.selectOnlyOneCustomer,
      self.selectOnlyOneSite,
      self.selectOnlyOneServiceLocation,
      (
        permissions,
        isMobile,
        contextAccepted,
        role,
        customerChosen,
        siteChosen,
        serviceLocationChosen,
        supplierChosen,
        onlyOneCustomer,
        onlyOneSite,
        onlyOneServiceLocation,
      ) => {
        if (permissions.status !== AsyncStatus.SUCCESS) return undefined;
        if (!role) return false;
        if (isRoleOfType(role, RoleType.Supplier)) return supplierChosen;
        if (isRoleOfType(role, RoleType.Customer) && isMobile) {
          if (contextAccepted) return true;
          if (customerChosen && siteChosen && serviceLocationChosen) return true;
          if ([ onlyOneCustomer, onlyOneSite, onlyOneServiceLocation ].some(({ status }) => status === AsyncStatus.LOADING)) return undefined;
        }

        return true;
      },
    );

    const selectHasRequiredSupplierContext = createSelector(
      self.selectPermissions,
      authFeature.selectIsAuthenticated,
      selectSupplierChosen,
      (permissions, isAuthenticated, supplierChosen) =>
        isAuthenticated || permissions.status !== AsyncStatus.SUCCESS ? supplierChosen : undefined,
    );

    const selectHasMultipleContexts = createSelector(
      self.selectIsMobile,
      authFeature.selectIsAdmin,
      authFeature.selectIsCentralTeam,
      authFeature.selectHasMultipleRoles,
      selectRole,
      selectHasMultipleCountries,
      authFeature.selectHasMultipleSuppliers,
      self.selectOnlyOneCustomer,
      self.selectOnlyOneSite,
      self.selectOnlyOneServiceLocation,
      (
        isMobile,
        isAdmin,
        isCentralTeam,
        hasMultipleRoles,
        role,
        hasMultipleCountries,
        hasMultipleSuppliers,
        onlyOneCustomer,
        onlyOneSite,
        onlyOneServiceLocation,
      ) => {
        if (isAdmin || isCentralTeam || hasMultipleRoles || hasMultipleCountries) return true;
        if (isRoleOfType(role, RoleType.Supplier)) return hasMultipleSuppliers;
        if (isRoleOfType(role, RoleType.Customer)) {
          if ([ onlyOneCustomer, onlyOneSite, onlyOneServiceLocation ].some((state) => state.status === AsyncStatus.SUCCESS && !state.data)) return true;
        }
        return false;
      });

    const selectRoles = createSelector(
      selectRole,
      authFeature.selectRoles,
      (role, roles) => role ? new Set([ role ]) : roles,
    );

    const selectHasContext = createSelector(
      selectHasMultipleContexts,
      authFeature.selectHasMultipleRoles,
      selectRole,
      selectCustomerChosen,
      selectSiteChosen,
      selectServiceLocationChosen,
      selectSupplierChosen,
      (
        hasMultipleContexts,
        hasMultipleRoles,
        role,
        customerChosen,
        siteChosen,
        serviceLocationChosen,
        supplierChosen,
      ) => {
        if (!hasMultipleContexts) return false;
        if (hasMultipleRoles && role) return true;
        return customerChosen || siteChosen || serviceLocationChosen || supplierChosen;
      },
    );

    const selectPermissions = createSelector(
      self.selectPermissions,
      asyncPermissions => new Set(pickAsyncData(asyncPermissions, [])),
    );

    const selectPermissionsLoaded = createSelector(
      self.selectPermissions,
      permissions => permissions.status === AsyncStatus.SUCCESS,
    );

    const selectCurrentSupplier = createSelector(
      selectRole,
      selectSupplier,
      (role, supplier) => isRoleOfType(role, RoleType.Supplier) ? supplier : null,
    );

    return {
      selectRole,
      selectSupplier,
      selectCustomer,
      selectCustomerChosen,
      selectSite,
      selectSiteChosen,
      selectServiceLocation,
      selectServiceLocationChosen,
      selectSupplierChosen,
      selectHasContext,
      selectHasRequiredContext,
      selectHasRequiredSupplierContext,
      selectHasMultipleContexts,
      selectRoles,
      selectPermissions,
      selectPermissionsLoaded,
      selectCustomerOption,
      selectSiteOption,
      selectServiceLocationOption,
      selectCurrentSupplier,
      selectCountry,
      selectHasMultipleCountries,
      selectCountryConfig,
      selectCountryCodes,
    };
  },
});
